import { useEffect } from "react";
import { NavLink } from "react-router-dom";
import { useIsInTeams } from "../TeamsHost";

export interface WebTab {
    label: string;
    path: string;
}

// Mirrors the static tabs in appPackage/manifest.json, which Teams shows in its own tab bar.
export const webTabs: WebTab[] = [
    { label: "Dashboard", path: "/tab" },
    { label: "pilOT", path: "/Assistant" },
    { label: "Sitemap", path: "/Factory" },
    { label: "O&I", path: "/Oi" },
    { label: "StopCoding", path: "/StopCoding" },
];

// Set on <html> while the bar is shown, so full-height views (h-screen) fit below it, see index.css.
const WEB_NAV_CLASS = "howru-web-nav";

export function WebTabBar({ tabs = webTabs }: { tabs?: WebTab[] }) {
    return (
        <nav
            aria-label="App tabs"
            className="sticky top-0 z-[9] flex h-[var(--web-nav-height)] items-stretch gap-1 overflow-x-auto px-4 bg-primary-light dark:bg-primary-dark text-text-light dark:text-text-dark border-b border-collapse-header-light dark:border-collapse-header-dark"
        >
            {tabs.map((tab) => (
                <NavLink
                    key={tab.path}
                    to={tab.path}
                    className={({ isActive }) =>
                        `flex items-center whitespace-nowrap px-3 text-sm border-b-2 ${isActive
                            ? "border-oi-button-blue font-semibold"
                            : "border-[color:transparent] hover:border-collapse-header-light dark:hover:border-collapse-header-dark"
                        }`
                    }
                >
                    {tab.label}
                </NavLink>
            ))}
        </nav>
    );
}

/**
 * Tab navigation for when the app is opened in a browser. Teams provides its own tab bar,
 * so nothing is rendered in Teams (or until it is known whether the app runs in Teams).
 */
export default function WebTabNavigation() {
    const inTeams = useIsInTeams();
    const visible = inTeams === false;

    useEffect(() => {
        if (!visible) {
            return;
        }

        document.documentElement.classList.add(WEB_NAV_CLASS);
        // Views that size themselves from the window (e.g. the Sitemap scene) re-measure on resize.
        window.dispatchEvent(new Event("resize"));
        return () => {
            document.documentElement.classList.remove(WEB_NAV_CLASS);
            window.dispatchEvent(new Event("resize"));
        };
    }, [visible]);

    return visible ? <WebTabBar /> : null;
}
